import { getApiBase, getToken } from "./auth";

export type Tier = "free" | "paid";

export interface AssetListItem {
  assetId: string;
  theme: string;
  voice: string;
  bed: string;
  length: number;
  timeOfDay: string;
  scriptVersion: number;
  variant: number;
  durationSec: number;
  tier: Tier;
  summary: string | null;
  qualityPassed: number | null;
  playCount: number;
  createdAt: string;
  publishedAt: string | null;
  plays7d: number;
  completionRate7d: number | null;
}

export interface AssetListResponse {
  items: AssetListItem[];
  nextCursor: string | null;
}

export interface AssetRow {
  asset_id: string;
  theme: string;
  voice: string;
  bed: string;
  length: number;
  time_of_day: string;
  script_version: number;
  variant: number;
  voice_config_id: string;
  bed_id: string;
  duration_sec: number;
  source: string;
  safety_reviewed: number;
  play_count: number;
  audio_key: string;
  transcript_key: string | null;
  tier: Tier;
  summary: string | null;
  quality_passed: number | null;
  quality_flags: string | null;
  quality_metrics: string | null;
  created_at: string;
  published_at: string | null;
}

export interface JobRow {
  job_id: string;
  asset_id: string;
  status: "queued" | "running" | "done" | "failed" | "needs_review";
  requested_by?: string;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
  params_json?: string;
}

export interface PlayRow {
  play_id: string;
  install_id: string;
  tier_at_play: Tier;
  device_kind: string;
  app_version: string | null;
  started_at: string;
  ended_at: string | null;
  progress_sec: number;
  duration_sec_at_play: number;
  completed: number;
  end_reason: string | null;
}

export interface AssetDetailResponse {
  asset: AssetRow;
  jobs: JobRow[];
  plays: { total: number; completed: number; paid: number; macos: number; d1: number; d7: number; d30: number };
  recentPlays: PlayRow[];
}

export interface QueueDepth {
  queued: number;
  running: number;
  done: number;
  failed: number;
  needs_review: number;
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken();
  const url = `${getApiBase()}${path}`;
  const headers = new Headers(init?.headers);
  if (token) headers.set("authorization", `Bearer ${token}`);
  const resp = await fetch(url, { ...init, headers });
  if (resp.status === 401) {
    const err: Error & { status?: number } = new Error("Unauthorized");
    err.status = 401;
    throw err;
  }
  if (!resp.ok) throw new Error(`${resp.status}: ${await resp.text()}`);
  return resp.json() as Promise<T>;
}

export interface AssetListFilters {
  theme?: string;
  voice?: string;
  length?: string;
  tier?: Tier | "";
  quality?: "passed" | "failed" | "unreviewed" | "";
  published?: "true" | "false" | "";
  cursor?: string | null;
  limit?: number;
}

export function listAssets(filters: AssetListFilters = {}): Promise<AssetListResponse> {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== "") q.set(k, String(v));
  }
  return apiFetch<AssetListResponse>(`/admin/assets?${q.toString()}`);
}

export function assetDetail(assetId: string): Promise<AssetDetailResponse> {
  return apiFetch<AssetDetailResponse>(`/admin/assets/${encodeURIComponent(assetId)}`);
}

export function listJobs(params: { status?: string; limit?: number } = {}): Promise<{ items: JobRow[] }> {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  if (params.limit)  q.set("limit", String(params.limit));
  return apiFetch(`/admin/jobs?${q.toString()}`);
}

export function queueDepth(): Promise<QueueDepth> {
  return apiFetch<QueueDepth>(`/admin/queue/depth`);
}

export function transcriptURL(assetId: string): string {
  return `${getApiBase()}/admin/transcripts/${encodeURIComponent(assetId)}`;
}

export function audioURL(assetId: string): string {
  return `${getApiBase()}/admin/assets/${encodeURIComponent(assetId)}/audio`;
}

// Operator actions — Phase 3.

export interface RerenderResult { jobId: string; assetId: string }

export function rerenderAsset(assetId: string): Promise<RerenderResult> {
  return apiFetch<RerenderResult>(`/admin/assets/${encodeURIComponent(assetId)}/rerender`, { method: "POST" });
}

export function republishAsset(assetId: string): Promise<{ version: number }> {
  return apiFetch(`/admin/assets/${encodeURIComponent(assetId)}/republish`, { method: "POST" });
}

export function unpublishAsset(assetId: string): Promise<{ version: number }> {
  return apiFetch(`/admin/assets/${encodeURIComponent(assetId)}/unpublish`, { method: "POST" });
}

export function patchAsset(
  assetId: string,
  body: { summary?: string | null; tier?: Tier; qualityPassed?: 0 | 1 | null }
): Promise<{ version: number }> {
  return apiFetch(`/admin/assets/${encodeURIComponent(assetId)}`, {
    method: "PATCH",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
}

export function retryJob(jobId: string): Promise<RerenderResult> {
  return apiFetch<RerenderResult>(`/admin/queue/retry/${encodeURIComponent(jobId)}`, { method: "POST" });
}

export function getJob(jobId: string): Promise<JobRow> {
  return apiFetch<JobRow>(`/admin/jobs/${encodeURIComponent(jobId)}`);
}

// Metrics — Phase 4.
export interface PlaysTimeSeriesPoint { day: string; total: number; completed: number; paid: number }
export interface PlaysMetrics {
  totals: { d1: number; d7: number; d30: number; completion7d: number | null };
  timeseries: PlaysTimeSeriesPoint[];
  byTheme: { theme: string; plays: number; completion: number | null }[];
  byVoice: { voice: string; plays: number; completion: number | null }[];
  byDevice: { device_kind: string; plays: number }[];
}
export interface ConversionMetrics {
  freeInstalls: number;
  paidInstalls: number;
  conversionRate: number | null;
  recentConversions: { install_id: string; converted_at: string }[];
}
export interface PipelineMetrics {
  successRate7d: number | null;
  totals: Record<string, number>;
  recentFailures: { job_id: string; asset_id: string; error: string | null; created_at: string }[];
}
export interface AffinityCell { theme: string; voice: string; plays: number; completionRate: number | null }

export function metricsPlays(range: "7d" | "30d" | "90d" = "30d"): Promise<PlaysMetrics> {
  return apiFetch(`/admin/metrics/plays?range=${range}`);
}
export function metricsConversion(): Promise<ConversionMetrics> {
  return apiFetch(`/admin/metrics/conversion`);
}
export function metricsPipeline(): Promise<PipelineMetrics> {
  return apiFetch(`/admin/metrics/pipeline`);
}
export function metricsAffinity(): Promise<AffinityCell[]> {
  return apiFetch(`/admin/metrics/affinity`);
}
